/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";

import type { TErrorResponse } from "../services/generalTypes";

// NestJS class-validator returns multiple violations as an array on `message`.
// Flatten to a single human-readable string so toasts/UIs don't render
// "[object Object]" or just one unhelpful message.
const normalizeMessage = (raw: unknown, fallback: string): string => {
  if (Array.isArray(raw)) {
    const parts = raw
      .map((m) => (typeof m === "string" ? m : (m as any)?.message))
      .filter(Boolean);
    return parts.length > 0 ? parts.join(". ") : fallback;
  }
  if (typeof raw === "string" && raw.trim()) return raw;
  return fallback;
};

export const transformErrorResponse = (error: any): TErrorResponse => {
  // Silently-recovered MFA-window rejections (Error('') + silent=true from the
  // axios interceptor / handleApiCall) must stay silent — inventing "An
  // unknown error occurred" here put a toast on every mobile sign-in.
  if (error?.silent === true || error?.code === "MFA_REQUIRED" || error?.error === "MFA_REQUIRED") {
    return { code: String(error?.code ?? "MFA_REQUIRED"), error: true, message: "", silent: true };
  }

  if (axios.isAxiosError(error) && error.response) {
    const responseData = error.response.data;
    return {
      code: responseData.statusCode?.toString() || responseData.code || "UNKNOWN_ERROR",
      error: true,
      message: normalizeMessage(responseData.message, "An unknown error occurred"),
    };
  }

  // Handle case where error is already the response data
  if (error && typeof error === "object" && error.message) {
    return {
      code: error.statusCode?.toString() || error.code || "UNKNOWN_ERROR",
      error: true,
      message: normalizeMessage(error.message, "An unknown error occurred"),
    };
  }

  // Handle unexpected errors
  return {
    code: "UNKNOWN_ERROR",
    error: true,
    message: "An unknown error occurred",
  };
};
